/**
 * Generates the three-language code examples on the docs pages (cURL /
 * JavaScript / Python), and a small real syntax highlighter for them
 * built for the docs pages specifically rather than reusing the
 * Playground's buildCodeSnippets(): that generator's cURL base URL
 * (https://api.askmila.com/v1/) doesn't match the real API's confirmed
 * base (see docs-api-reference.ts's own header comment), and it only
 * covers Ruby/TypeScript/cURL, not the JavaScript/Python pair shown here.
 *
 * The highlighter is one regex pass over the whole snippet approximate,
 * not a real parser, which is the standard, sufficient approach for
 * illustrative doc examples. It wraps tokens in the .code-keyword/
 * .code-str/.code-num/.code-bool/.code-blue spans already defined in
 * styles.scss for exactly this (previously unused anywhere). Output is
 * trusted HTML, safe to bypass sanitization for, since every input to it
 * originates from the static example payloads below, never user input.
 */

export type DocsLang = 'curl' | 'javascript' | 'python';

export const DOCS_BASE_URL = 'http://api.mila.test:3000/v1';

const KEYWORDS: Record<DocsLang, string[]> = {
  curl: ['curl', '-X', '-H', '-d', 'POST'],
  javascript: ['const', 'await', 'async', 'function', 'return', 'import', 'from', 'new'],
  python: ['import', 'def', 'return'],
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** One pass, in a fixed order: JSON-style "key": (blue) or else any other
 *  quoted string (green) as a single regex with two alternatives, not
 *  two separate replace() calls. Each replace() runs over whatever
 *  string the previous step left behind, HTML it just inserted
 *  included, not just the original code: a separate "any quoted string"
 *  pass after the key pass would re-scan class="code-blue" (a quoted
 *  string too, as far as that regex can tell) and wrap the key
 *  regex's own attribute value in a second, malformed span, corrupting
 *  every "key": in the output. One pass over the original text, picking
 *  the key template when the match has a trailing ":" and the string
 *  template otherwise, never gives a later step generated markup to
 *  re-match. Then numbers, then booleans/None/null, then this
 *  language's own keywords none of those match `"`, so they're safe as
 *  separate passes. */
export function highlightCode(code: string, lang: DocsLang): string {
  let out = escapeHtml(code);

  out = out.replace(/"([^"]+)":|"([^"]*)"/g, (_m, key: string | undefined, str: string | undefined) =>
    key !== undefined
      ? `<span class="code-blue">"${key}"</span>:`
      : `<span class="code-str">"${str}"</span>`,
  );
  out = out.replace(/:\s(-?\d+(?:\.\d+)?)/g, ': <span class="code-num">$1</span>');
  out = out.replace(/\b(true|false|True|False)\b/g, '<span class="code-bool">$1</span>');
  out = out.replace(/\b(null|None)\b/g, '<span class="code-op">$1</span>');

  for (const kw of KEYWORDS[lang]) {
    const escaped = kw.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    out = out.replace(new RegExp(`(?<!["/])\\b${escaped}\\b(?!["<])`, 'g'), `<span class="code-keyword">${kw}</span>`);
  }

  return out;
}

/** Pretty-printed, syntax-highlighted JSON used for the standalone
 *  "Request body" block (no call wrapper, just the envelope). */
export function highlightJson(value: unknown): string {
  return highlightCode(JSON.stringify(value, null, 2), 'curl'); // 'curl'-family highlighting has no keywords to misfire on plain JSON
}

export interface DocsPayload {
  request: object;
  response: object;
}

/** Python-literal rendering of a JSON-shaped payload True/False/None
 *  instead of true/false/null, everything else identical to JSON. */
function pyDict(value: unknown): string {
  return JSON.stringify(value, null, 4)
    .replace(/: true/g, ': True')
    .replace(/: false/g, ': False')
    .replace(/: null/g, ': None');
}

/** Builds the full call example (auth header, method, body) in each of
 *  the three languages, for a given endpoint path `path` is the full
 *  path already (e.g. "/api/rag"), appended straight to
 *  DOCS_BASE_URL. */
export function docsCallExamples(path: string, payload: DocsPayload): Record<DocsLang, string> {
  const url = `${DOCS_BASE_URL}${path}`;
  const bodyJson = JSON.stringify(payload, null, 2);

  const curl =
`curl -X POST "${url}" \\
  -H "Authorization: Bearer cks_live_…" \\
  -H "Content-Type: application/json" \\
  -d '${bodyJson}'`;

  const javascript =
`const res = await fetch("${url}", {
  method: "POST",
  headers: {
    Authorization: "Bearer cks_live_…",
    "Content-Type": "application/json",
  },
  body: JSON.stringify(${bodyJson.split('\n').join('\n  ')}),
});

const data = await res.json();`;

  const python =
`import requests

response = requests.post(
    "${url}",
    headers={"Authorization": "Bearer cks_live_…"},
    json=${pyDict(payload)},
)

data = response.json()`;

  return {
    curl: highlightCode(curl, 'curl'),
    javascript: highlightCode(javascript, 'javascript'),
    python: highlightCode(python, 'python'),
  };
}

/** Un-does highlightCode()'s span-wrapping and entity-escaping what the
 *  copy button puts on the clipboard, since the highlighted HTML itself
 *  isn't what a developer wants pasted into their editor. */
export function stripHighlighting(html: string): string {
  return html.replace(/<[^>]+>/g, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

/** navigator.clipboard only exists in a secure context https, or the
 *  literal hostname "localhost". A custom dev domain like app.mila.test
 *  served over plain http doesn't qualify even though /etc/hosts points
 *  it at 127.0.0.1, so navigator.clipboard is undefined there, and the
 *  three docs copy buttons (quickstart, making-a-call, the API reference
 *  detail page) all called `navigator.clipboard?.writeText(...).then(...)`
 *  the `?.` swallows the whole chain including .then() when the API
 *  is missing, so the click silently did nothing instead of erroring or
 *  falling back.
 *
 *  Falls back to the old execCommand('copy') path (an off-screen
 *  textarea: create, select, copy, remove), which works regardless of
 *  secure-context status every browser still supports it, deprecated
 *  or not, specifically for this case. */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Falls through to the legacy path below some browsers still
      // refuse writeText() from a context lacking clipboard-write
      // permission even when the API itself exists.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
}
